import React from 'react';
import useGame from '../../contexts/useGame';
import Tooltip from '../Tooltip/Tooltip';
import ActiveTalentTreeNode from '../ActiveTalentTreeNode/ActiveTalentTreeNode';
import PassiveTalentTreeNode from '../PassiveTalentTreeNode/PassiveTalentTreeNode';
import TalentTreePercentBadge from '../TalentTreePercentBadge/TalentTreePercentBadge';
import {
  RetailWarcraftTalentTreeBuildNode,
  RetailWarcraftTalentTreeEntry,
  RetailWarcraftTalentTreeEntryType,
  RetailWarcraftTalentTreeType,
} from '../../typings/viewModels/retailWarcraftTalentTreeBuildNode';
import {
  heatMapPercentageClassName,
  heatMapPercentageForEntry,
} from '../TalentTreeNode/TalentTreeNode';
import { createTalentTreeUrl } from '../../helpers/talentTreeUrl';

export type ChoiceTalentTreeNodeProps = {
  showChoices: boolean;
  tooltipClassName?: string;
  node: RetailWarcraftTalentTreeBuildNode;
  spellModifierId: number;
};

export default function ChoiceTalentTreeNode({
  showChoices,
  tooltipClassName,
  node,
  spellModifierId,
}: ChoiceTalentTreeNodeProps): JSX.Element {
  const game = useGame();
  const {
    entry: selectedSpell,
    heatMapPercent: percent,
    node: { abilities: choices },
  } = node;

  const isHeatMap = !!percent;

  const useTotalPercentage =
    node.node.treeType === RetailWarcraftTalentTreeType.HeroTree
      ? false
      : isHeatMap;

  const displayPercentage =
    !selectedSpell || useTotalPercentage
      ? percent?.values.reduce((a, b) => a + b, 0) ?? 0
      : heatMapPercentageForEntry(node, selectedSpell);
  const heatMapClassName = heatMapPercentageClassName(displayPercentage);

  if (!selectedSpell) {
    return (
      <span
        className={`talent-tree-node talent-tree-node-choice talent-tree-node--inactive ${
          isHeatMap ? `talent-tree-node--${heatMapClassName}` : ''
        }`}
      >
        <ConditionalChoicesTooltip
          node={node}
          active={showChoices}
          className={tooltipClassName}
          tooltipTriggerClassName='talent-tree-node-choice__tooltip-trigger--inactive'
          isDisplayingPercent={percent !== null}
          spellModifierId={spellModifierId}
        >
          {choices.map((choice) => {
            const img = (
              <img
                key={choice.spellId}
                src={`${game?.assetServerBaseUrl}/img/warcraft/abilities/${choice.icon}.jpg`}
                alt={choice.name}
                loading='lazy'
              />
            );

            if (!showChoices) {
              return (
                <a
                  target='_blank'
                  rel='noreferrer'
                  href={createTalentTreeUrl(
                    choice.spellId,
                    choice.definitionId,
                    node.pointsInvested,
                    spellModifierId,
                    game?.abilityExternalSitePath
                  )}
                  key={choice.spellId}
                >
                  {img}
                </a>
              );
            }

            return img;
          })}
        </ConditionalChoicesTooltip>
        {isHeatMap ? (
          <TalentTreePercentBadge percent={displayPercentage} />
        ) : null}
        <div className='talent-tree-node-choice__overlay' />
      </span>
    );
  }

  const Tag = percent ? 'span' : 'a';

  return (
    <Tag
      className={`talent-tree-node talent-tree-node-choice talent-tree-node-choice--selected ${
        isHeatMap ? `talent-tree-node--${heatMapClassName}` : ''
      }`}
      target={percent ? undefined : '_blank'}
      rel={percent ? undefined : 'noreferrer'}
      href={
        percent
          ? undefined
          : createTalentTreeUrl(
              selectedSpell.spellId,
              selectedSpell.definitionId,
              node.pointsInvested,
              spellModifierId,
              game?.abilityExternalSitePath
            )
      }
      onClick={(e) => e.stopPropagation()}
    >
      <ConditionalChoicesTooltip
        node={node}
        active={showChoices}
        className={tooltipClassName}
        tooltipTriggerClassName='talent-tree-node-choice__tooltip-trigger--active'
        isDisplayingPercent={percent !== null}
        spellModifierId={spellModifierId}
      >
        <>
          <img
            src={`${game?.assetServerBaseUrl}/img/warcraft/abilities/${selectedSpell.icon}.jpg`}
            alt={selectedSpell.name}
            loading='lazy'
          />
          {isHeatMap ? (
            <TalentTreePercentBadge percent={displayPercentage} />
          ) : null}
        </>
      </ConditionalChoicesTooltip>
      <div
        className={`talent-tree-node-choice__overlay ${
          isHeatMap
            ? `talent-tree-node-choice__overlay--${heatMapClassName}`
            : ''
        }`}
      />
    </Tag>
  );
}

type NodeComponent = (props: {
  node: RetailWarcraftTalentTreeBuildNode;
  entry: RetailWarcraftTalentTreeEntry;
  spellModifierId: number;
}) => JSX.Element;

const componentTypeMap: Record<
  RetailWarcraftTalentTreeEntry['type'],
  NodeComponent
> = {
  [RetailWarcraftTalentTreeEntryType.Active]: ActiveTalentTreeNode,
  [RetailWarcraftTalentTreeEntryType.Passive]: PassiveTalentTreeNode,
};

type ConditionalChoicesTooltipProps = {
  node: RetailWarcraftTalentTreeBuildNode;
  children: JSX.Element | (JSX.Element | null)[];
  tooltipTriggerClassName?: string;
  active: boolean;
  className?: string;
  isDisplayingPercent: boolean;
  spellModifierId: number;
  nodeComponent?: NodeComponent;
};

export function ConditionalChoicesTooltip({
  active,
  children,
  tooltipTriggerClassName,
  node,
  className,
  isDisplayingPercent,
  spellModifierId,
  nodeComponent: NodeComponent,
}: ConditionalChoicesTooltipProps): JSX.Element {
  if (!active) {
    return <span className={tooltipTriggerClassName}>{children}</span>;
  }

  return (
    <Tooltip
      triggerProps={{
        className: tooltipTriggerClassName,
      }}
      tooltipClassName='talent-tree-node-choice__tooltip'
      allowHover
      placementOptions={{
        allowedPlacements: ['top'],
      }}
      content={
        <div
          className={`talent-tree-node-choice__tooltip--body ${className} ${
            isDisplayingPercent
              ? 'talent-tree-node-choice__tooltip--body-heatmap'
              : ''
          }`}
        >
          {node.node.abilities.map((entry) => {
            const Component = NodeComponent ?? componentTypeMap[entry.type];

            return (
              <Component
                node={node}
                entry={entry}
                key={entry.spellId}
                spellModifierId={spellModifierId}
              />
            );
          })}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
}
